import React, { useState, useEffect, useRef } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useApp } from "../context/AppContext";
import Sidebar from "../components/Sidebar";
import PageShell from "../components/PageShell";
import { KPI, PageAlert, Tag } from "../components/atoms";
import { getHeatmap, getTopCountries, getTopCategories, getCompanies, getShows, getShow, getEventsWithData, getCrossEventSummary, generateMarketRead } from "../services/api";
import { BarChart2, Building2, Globe, Package, Star, Trophy, Info, AlertTriangle } from "lucide-react";
import AiSignals from "../components/AiSignals";
import { INSIGHTS_AI_READS_ENABLED, SHOW_YOY_VIEW_ENABLED } from "../config/featureFlags";
import InsightsGeoSection from "./InsightsGeoSection";
import ShowYoYDashboard from "./ShowYoYDashboard";
import { buildShowDetail } from "../utils/showDetail";
import { useDefaultEvent } from "../hooks/useDefaultEvent";
import { EventSelect } from "../components/EventSelect";
import { FilterBar, FilterSelect, FilterClear, filterSelectStyle } from "../components/FilterBar";

const F = "'Montserrat',sans-serif";
const M = "'Montserrat',sans-serif";

// Colour per "Market read" signal type (demand/market lens).
const MARKET_COLOR = { concentration: "#E8431A", category: "#F18F18", geography: "#960659", cross_event: "#5F1156" };


function heatColor(v) {
  if (v >= 1000) return "#26262A";
  if (v >= 700)  return "#E8431A";
  if (v >= 400)  return "#E8431A";
  if (v >= 200)  return "#F3A78C";
  if (v >= 100)  return "#F7C9B8";
  if (v > 0)     return "#FCEBE5";
  return "#FAFAFA";
}

const EmptyState = ({ message = "No data available yet" }) => (
  <div style={{padding:"32px 0",textAlign:"center",color:"#818084"}}>
    <div style={{fontSize:28,marginBottom:6,display:"flex",justifyContent:"center"}}><BarChart2 size={28} color="#818084" /></div>
    <div style={{fontSize:12,fontWeight:600,color:"#818084"}}>{message}</div>
    <div style={{fontSize:10,marginTop:4}}>Data populates after enrichment jobs complete</div>
  </div>
);

export default function InsightsPage() {
  const {
    selectedEvent, setSelectedEvent, changeEvent, nav,
    currentRole, setCurrentRole,
    notifs, setNotifs, notifOpen, setNotifOpen,
    events,
    pendingInsights, setPendingInsights,
  } = useApp();

  useDefaultEvent();

  const [heatmapRaw,    setHeatmapRaw]    = useState([]);
  const [topCountries,  setTopCountries]  = useState([]);
  const [topCategories, setTopCategories] = useState([]);
  const [crossEventData, setCrossEventData] = useState([]);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [loading, setLoading] = useState(true);

  // "Market read" — on-demand AI market/demand analysis (grounded in the numbers)
  const [marketRead,    setMarketRead]    = useState(null);
  const [marketLoading, setMarketLoading] = useState(false);
  const [marketError,   setMarketError]   = useState(null);

  const CURRENT_YEAR = new Date().getFullYear();

  // ── Local filter state ────────────────────────────────────────────────────
  const [filterEvent,    setFilterEvent]    = useState(selectedEvent || "");
  const [filterYear,     setFilterYear]     = useState(String(CURRENT_YEAR));
  const [filterShow,     setFilterShow]     = useState("");
  const [shows,          setShows]          = useState([]);
  const [eventYears,     setEventYears]     = useState([]); // years that actually have shows for the event
  const [resolvedEvent,  setResolvedEvent]  = useState(""); // event whose year options have loaded — gates data fetch to avoid a stale-year flicker
  const [eventsWithData, setEventsWithData] = useState(null); // null = loading
  const [insightsTab,    setInsightsTab]    = useState("overview");

  // Selected-show detail (metadata + Year-on-Year data) — loaded when a single show is picked
  const [showMeta, setShowMeta] = useState(null);
  const [showView, setShowView] = useState("edition"); // "edition" | "yoy"
  const pendingShowRef = useRef(null);                  // show_id to apply once its shows list loads
  const skipAutoYearRef = useRef(false);                // true when a deep-link handoff already fixed the year

  // Load once: which ITE events have competitor data
  useEffect(() => {
    getEventsWithData()
      .then(ids => setEventsWithData(new Set(Array.isArray(ids) ? ids : [])))
      .catch(() => setEventsWithData(new Set()));
  }, []);

  // Consume a pending-insights handoff (e.g. clicking a show in Competitor Tracker):
  // seed event + year now, and remember the show to apply once its shows list arrives.
  useEffect(() => {
    if (!pendingInsights) return;
    const { ite_event_id, show_id, year } = pendingInsights;
    if (ite_event_id) { setFilterEvent(ite_event_id); setSelectedEvent(ite_event_id); }
    if (year != null && year !== "") { setFilterYear(String(year)); skipAutoYearRef.current = true; }
    pendingShowRef.current = show_id || null;
    setPendingInsights(null);
  }, [pendingInsights]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply the pending show once it exists in the loaded shows list (event/year effects
  // reset filterShow to "" first, so this runs after they settle).
  useEffect(() => {
    if (pendingShowRef.current && shows.some(s => s.show_id === pendingShowRef.current)) {
      setFilterShow(pendingShowRef.current);
      pendingShowRef.current = null;
    }
  }, [shows]);

  // Load the selected show's metadata + YoY data whenever a single show is chosen.
  useEffect(() => {
    if (!filterShow) { setShowMeta(null); setShowView("edition"); return; }
    let alive = true;
    getShow(filterShow)
      .then(s => { if (alive) { setShowMeta(buildShowDetail(s)); setShowView("edition"); } })
      .catch(() => { if (alive) setShowMeta(null); });
    return () => { alive = false; };
  }, [filterShow]);

  // Sync filter when sidebar context event changes
  useEffect(() => {
    setFilterEvent(selectedEvent || "");
    setFilterShow("");
  }, [selectedEvent]);

  // Load shows scoped to the selected event AND year.
  // Wait until the year has been resolved for this event (resolvedEvent === filterEvent)
  // so this fetch runs once with the correct year instead of firing on the stale year.
  useEffect(() => {
    if (!filterEvent) { setShows([]); setFilterShow(""); return; }
    if (resolvedEvent !== filterEvent) return;
    const params = { ite_event_id: filterEvent, limit: 200 };
    if (filterYear) params.year = Number(filterYear);
    getShows(params)
      .then(data => { setShows(Array.isArray(data) ? data : (data?.items || [])); setFilterShow(""); })
      .catch(() => setShows([]));
  }, [filterEvent, filterYear, resolvedEvent]);

  // Derive the Year filter options from the selected event's actual shows (so only
  // real years appear, not a fixed calendar range), and default to the latest year
  // that has data. A deep-link handoff that already fixed the year takes precedence.
  useEffect(() => {
    if (!filterEvent) { setEventYears([]); setResolvedEvent(""); return; }
    const applyDefault = !skipAutoYearRef.current;
    skipAutoYearRef.current = false;
    let alive = true;
    getShows({ ite_event_id: filterEvent, limit: 200 })
      .then(data => {
        if (!alive) return;
        const list  = Array.isArray(data) ? data : (data?.items || []);
        const years = [...new Set(list.map(s => Number(s.year)).filter(Boolean))].sort((a, b) => b - a);
        setEventYears(years);
        if (applyDefault && years.length) {
          const dataYears = list.filter(s => s.year && (s.total_companies || 0) > 0).map(s => Number(s.year));
          setFilterYear(String(dataYears.length ? Math.max(...dataYears) : years[0]));
        }
        // Year is now settled for this event — unblock the scoped shows + data fetches
        // (batched with setFilterYear above, so they run once with the correct year).
        setResolvedEvent(filterEvent);
      })
      .catch(() => { if (alive) { setEventYears([]); setResolvedEvent(filterEvent); } });
    return () => { alive = false; };
  }, [filterEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Data fetch — requires an event selection ──────────────────────────────
  // Gated on resolvedEvent so it fires once with the resolved year (no stale-year
  // fetch → no flicker).
  useEffect(() => {
    if (!filterEvent) {
      setHeatmapRaw([]); setTopCountries([]); setTopCategories([]);
      setTotalCompanies(0);
      setLoading(false);
      return;
    }
    if (resolvedEvent !== filterEvent) { setLoading(true); return; }
    const p = { ite_event_id: filterEvent };
    if (filterYear) p.year    = Number(filterYear);
    if (filterShow) p.show_id = filterShow;
    setLoading(true);
    Promise.all([
      getHeatmap(p).catch(() => []),
      getTopCountries(p).catch(() => []),
      getTopCategories(p).catch(() => []),
      getCompanies({ limit: 1 }).catch(() => ({ total: 0 })),
    ]).then(([heat, countries, cats, companies]) => {
      setHeatmapRaw(Array.isArray(heat) ? heat : []);
      setTopCountries(Array.isArray(countries) ? countries : []);
      setTopCategories(Array.isArray(cats) ? cats : []);
      const total = Array.isArray(companies) ? companies.length : (companies?.total ?? 0);
      setTotalCompanies(total);
      setLoading(false);
    });
  }, [filterEvent, filterYear, filterShow, resolvedEvent]);

  // ── Cross-event fetch — only refetches when event changes ─────────────────
  useEffect(() => {
    if (!filterEvent) { setCrossEventData([]); return; }
    getCrossEventSummary({ ite_event_id: filterEvent })
      .then(d => setCrossEventData(Array.isArray(d) ? d : []))
      .catch(() => setCrossEventData([]));
  }, [filterEvent]);

  // Clear any generated market read when the filter scope changes (avoid stale AI).
  useEffect(() => { setMarketRead(null); setMarketError(null); }, [filterEvent, filterYear, filterShow]);

  // ── Empty-state detection ─────────────────────────────────────────────────
  const hasData       = heatmapRaw.length > 0 || topCountries.length > 0 || topCategories.length > 0 || crossEventData.length > 0;
  const YEAR_OPTIONS  = eventYears; // dynamic — only years that have shows for the selected event
  // The Year is derived from the selected event, so a filter is only meaningfully
  // "active" once an event (or a specific show) is chosen.
  const filtersActive = !!(filterEvent || filterShow);

  // Generate the "Market read" from the current (filtered) numbers.
  const runMarketRead = () => {
    setMarketLoading(true);
    setMarketError(null);
    const heatmapTop = [...heatmapRaw]
      .sort((a, b) => (b.exhibitor_count || 0) - (a.exhibitor_count || 0))
      .slice(0, 8)
      .map(r => ({ country: r.country, category: r.category_name, count: r.exhibitor_count || 0 }));
    generateMarketRead({
      scope: filterEvent || "all events",
      year: filterYear || "all",
      total_exhibitors: topCountries.reduce((s, r) => s + (r.total_exhibitors || 0), 0),
      top_countries: topCountries.slice(0, 6).map(r => ({ name: r.country, count: r.total_exhibitors || 0 })),
      top_categories: topCategories.slice(0, 6).map(r => ({ name: r.category_name, count: r.total_exhibitors || 0 })),
      heatmap_top: heatmapTop,
      cross_events: crossEventData.slice(0, 8).map(r => ({ name: r.other_event_name, count: r.company_count || 0 })),
    })
      .then(res => {
        if (res?.error) {
          setMarketError(String(res.error).includes("not configured")
            ? "AI isn't configured on this environment."
            : "Couldn't generate — please retry.");
          setMarketRead(null);
        } else {
          setMarketRead(Array.isArray(res?.signals) ? res.signals : []);
        }
      })
      .catch(() => setMarketError("Couldn't reach the AI service."))
      .finally(() => setMarketLoading(false));
  };

  // ── Derived: heatmap pivot ─────────────────────────────────────────────────
  const heatByCountry = {};
  const catTotals = {};
  heatmapRaw.forEach(r => {
    if (!heatByCountry[r.country]) heatByCountry[r.country] = {};
    heatByCountry[r.country][r.category_name] = (heatByCountry[r.country][r.category_name] || 0) + (r.exhibitor_count || 0);
    catTotals[r.category_name] = (catTotals[r.category_name] || 0) + (r.exhibitor_count || 0);
  });
  const catHeaders = [...new Set(heatmapRaw.map(r => r.category_name))]
    .sort((a, b) => (catTotals[b] || 0) - (catTotals[a] || 0));
  const pivotedHeatmap = Object.entries(heatByCountry)
    .map(([country, cats]) => ({
      c: country,
      cats: catHeaders.map(h => cats[h] || 0),
      total: catHeaders.reduce((s, h) => s + (cats[h] || 0), 0),
    }))
    .sort((a, b) => b.total - a.total);

  // ── Derived: KPIs ─────────────────────────────────────────────────────────
  const totalExhibitors = topCountries.reduce((s, r) => s + (r.total_exhibitors || 0), 0) || totalCompanies;
  const countryCount    = topCountries.length;
  const categoryCount   = topCategories.length;
  const topCat          = topCategories[0] || null;
  const topCountry      = topCountries[0] || null;

  // ── Derived: cross-event chart — already grouped by ITE event from backend ─
  const crossEventChart = crossEventData
    .map(r => ({ event: r.other_event_name, count: r.company_count }))
    .slice(0, 10);

  // ── Derived: selected-show YoY series ─────────────────────────────────────
  const showYears     = showMeta ? [...(showMeta.yearData || [])].sort((a, b) => a.y - b.y) : [];
  const showLatest    = showYears.length ? showYears[showYears.length - 1] : null;
  const showPrev      = showYears.length > 1 ? showYears[showYears.length - 2] : null;
  const showMultiYear = SHOW_YOY_VIEW_ENABLED && showYears.length > 1;
  const yoyActive     = SHOW_YOY_VIEW_ENABLED && !!showMeta && showMultiYear && showView === "yoy";

  return (
    <PageShell>
      <style>{`*::-webkit-scrollbar{width:5px}*::-webkit-scrollbar-thumb{background:#D8DADC;border-radius:3px}table.ct{width:100%;border-collapse:collapse;font-size:11px}table.ct th{text-align:left;padding:8px 10px;font-size:9px;font-weight:700;color:#818084;text-transform:uppercase;letter-spacing:0.4px;border-bottom:2px solid #D8DADC;background:#FAFAFA}table.ct td{padding:8px 10px;border-bottom:1px solid #F4F4F5}`}</style>

      <Sidebar active="insights" onNav={nav}
        selectedEvent={selectedEvent} onChangeEvent={changeEvent}
        notifs={notifs} setNotifs={setNotifs} notifOpen={notifOpen} setNotifOpen={setNotifOpen}
        currentRole={currentRole} setCurrentRole={setCurrentRole}/>

      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>

        {/* Header */}
        <div style={{padding:"16px 28px",background:"#fff",borderBottom:"1px solid #D8DADC"}}>
          <div style={{fontSize:20,fontWeight:800}}>Market &amp; Competitor Insights</div>
          <div style={{fontSize:11,color:"#818084",marginTop:2}}>
            Understand competitor show growth, country/category movement, exhibitor changes, and market gaps across years.
          </div>
        </div>

        {/* Filter bar */}
        <FilterBar>
          <EventSelect
            value={filterEvent}
            onChange={e => { const v = e.target.value; setFilterEvent(v); setSelectedEvent(v || null); }}
            events={events}
            eventsWithData={eventsWithData}
            style={filterSelectStyle(!!filterEvent)}
          />

          <FilterSelect value={filterYear} onChange={e => setFilterYear(e.target.value)} active={!!filterYear}>
            <option value="">Year: All</option>
            {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
          </FilterSelect>

          <FilterSelect
            value={filterShow}
            onChange={e => setFilterShow(e.target.value)}
            active={!!filterShow}
            disabled={!filterEvent || shows.length === 0}>
            <option value="">Competitor Show: All</option>
            {shows.map(s => <option key={s.show_id} value={s.show_id}>{s.name}</option>)}
          </FilterSelect>

          {(filterEvent || filterShow) && (
            <FilterClear onClick={() => { setFilterEvent(""); setFilterYear(""); setFilterShow(""); setSelectedEvent(null); }} />
          )}
        </FilterBar>

        {/* Tab strip */}
        <div style={{ display:"flex", background:"#fff", borderBottom:"1px solid #D8DADC", padding:"0 28px" }}>
          {[
            { id:"overview",     label:"Market Overview"        },
            { id:"geopolitical", label:"Geopolitical Alignment" },
          ].map(t => (
            <button key={t.id} onClick={() => setInsightsTab(t.id)} style={{
              padding:"10px 16px", background:"none", border:"none",
              borderBottom: insightsTab===t.id ? "2px solid #E8431A" : "2px solid transparent",
              color:      insightsTab===t.id ? "#E8431A" : "#818084",
              fontWeight: insightsTab===t.id ? 700 : 400,
              fontSize:12, fontFamily:F, cursor:"pointer", marginBottom:-1,
            }}>
              {t.label}
            </button>
          ))}
        </div>

        <div style={{flex:1,overflow:"auto",padding:"16px 28px 32px"}}>

          {insightsTab === "overview" && (<>

          {/* Market read — on-demand AI market/demand analysis (grounded in the numbers) */}
          {INSIGHTS_AI_READS_ENABLED && hasData && (
            <div style={{ background:"#fff", borderRadius:10, border:"1px solid #D8DADC", padding:"14px 18px", marginBottom:14 }}>
              <AiSignals
                title="Market read"
                signals={marketRead}
                loading={marketLoading}
                error={marketError}
                onGenerate={runMarketRead}
                colorByType={MARKET_COLOR}
                emptyText="AI market insights from the heatmap, top countries/categories & cross-event data — click Generate."
              />
            </div>
          )}

          {/* ── View toggle for multi-year shows: This Edition | Year on Year ─ */}
          {showMeta && showMultiYear && (
            <div style={{display:"flex",gap:6,marginBottom:14}}>
              {[{id:"edition",label:`${showLatest?.y ?? ""} Edition`},{id:"yoy",label:"Year on Year"}].map(v => (
                <button key={v.id} onClick={() => setShowView(v.id)}
                  style={{padding:"6px 14px",borderRadius:8,border:`1px solid ${showView===v.id?"#E8431A":"#D8DADC"}`,
                    background:showView===v.id?"#E8431A":"#fff",color:showView===v.id?"#fff":"#818084",
                    fontSize:11,fontWeight:showView===v.id?700:500,cursor:"pointer",fontFamily:F}}>
                  {v.label}
                </button>
              ))}
            </div>
          )}

          {yoyActive ? (
            <ShowYoYDashboard sh={showMeta} sortedYears={showYears} latest={showLatest} prev={showPrev}/>
          ) : (<>

          {/* ── Empty state: no data after load ───────────────────────────── */}
          {!loading && !hasData && (
            <PageAlert
              type={filtersActive ? "warning" : "info"}
              icon={filtersActive ? AlertTriangle : Info}
              title={filtersActive
                ? "No data found for the selected filters"
                : "Select a filter to explore insights"}
              message={filtersActive
                ? "There are no competitor exhibitor records for this combination yet. Try a different ITE event, year, or competitor show — data populates after enrichment runs."
                : "Use the filters above to choose an ITE event, year, or specific competitor show. Country heatmaps, category rankings, growth trends, and opportunity gaps will appear here."}
            />
          )}

          {/* ── Charts: only rendered when data exists or still loading ───── */}
          {(loading || hasData) && <>

          {/* KPI strip */}
          <div style={{display:"flex",gap:10,marginBottom:16}}>
            <KPI icon={<Building2 size={22} color="#E8431A" />}
              value={loading ? "…" : totalExhibitors.toLocaleString()}
              label="Total Companies"
              sub="across tracked shows"
              color="#E8431A"/>
            <KPI icon={<Globe size={22} color="#960659" />}
              value={loading ? "…" : countryCount || "—"}
              label="Countries"
              sub={countryCount ? "in competitor shows" : "no aggregate data yet"}
              color="#960659"/>
            <KPI icon={<Package size={22} color="#F18F18" />}
              value={loading ? "…" : categoryCount || "—"}
              label="Product Categories"
              sub={categoryCount ? "tracked" : "no aggregate data yet"}
              color="#F18F18"/>
            <KPI icon={<Star size={22} color="#22c55e" />}
              value={loading ? "…" : topCat ? topCat.total_exhibitors?.toLocaleString() : "—"}
              label={topCat ? topCat.category_name : "no data yet"}
              sub="Top Category"
              color="#22c55e"
              wrap/>
            <KPI icon={<Trophy size={22} color="#C1121F" />}
              value={loading ? "…" : topCountry ? topCountry.total_exhibitors?.toLocaleString() : "—"}
              label={topCountry ? topCountry.country : "no data yet"}
              sub="Top Country"
              color="#C1121F"
              wrap/>
          </div>

          {/* Heatmap — full width */}
          <div style={{background:"#fff",borderRadius:10,border:"1px solid #D8DADC",padding:"16px 18px",marginBottom:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
              <div style={{fontSize:13,fontWeight:700}}>Country × Product Category Heatmap</div>
              <div style={{display:"flex",alignItems:"center",gap:4,fontSize:9,color:"#818084"}}>
                Low
                <div style={{display:"flex",gap:1,marginLeft:4}}>
                  {["#FCEBE5","#F7C9B8","#F3A78C","#E8431A","#E8431A","#26262A"].map((c, i) => (
                    <div key={i} style={{width:16,height:10,background:c,borderRadius:1}}/>
                  ))}
                </div>
                High
              </div>
            </div>
            <div style={{fontSize:9,color:"#818084",marginBottom:10}}>
              Each cell shows exhibitor count for that country × category pairing. One company can appear in multiple categories, so row totals will exceed unique company counts.
            </div>
            {pivotedHeatmap.length === 0 ? <EmptyState message="Heatmap data not yet available"/> : (
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:10}}>
                  <thead>
                    <tr>
                      <th style={{textAlign:"left",padding:"6px 8px",fontSize:9,color:"#818084",fontWeight:700,whiteSpace:"nowrap"}}>Country / Category</th>
                      {catHeaders.map(h => (
                        <th key={h} style={{padding:"6px 4px",fontSize:8,color:"#818084",fontWeight:600,textAlign:"center",whiteSpace:"nowrap",maxWidth:80,overflow:"hidden",textOverflow:"ellipsis"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pivotedHeatmap.map((row, i) => (
                      <tr key={i}>
                        <td style={{padding:"6px 8px",fontWeight:600,whiteSpace:"nowrap"}}>{row.c}</td>
                        {row.cats.map((v, j) => (
                          <td key={j} style={{padding:"3px"}}>
                            <div style={{background:heatColor(v),color:v>=400?"#fff":"#26262A",padding:"4px 2px",borderRadius:3,fontSize:10,fontWeight:600,fontFamily:M,textAlign:"center"}}>
                              {v > 0 ? v.toLocaleString() : ""}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>

            {/* Top 5 Product Categories */}
            <div style={{background:"#fff",borderRadius:10,border:"1px solid #D8DADC",padding:"16px 18px"}}>
              <div style={{fontSize:13,fontWeight:700,marginBottom:4}}>Top 5 Product Categories</div>
              <div style={{fontSize:9,color:"#818084",marginBottom:10}}>By exhibitor count in competitor shows</div>
              {topCategories.length === 0
                ? <EmptyState message="No category data yet"/>
                : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={topCategories.slice(0,5)} layout="vertical"
                      margin={{left:4,right:20,top:0,bottom:0}} barCategoryGap="20%">
                      <XAxis type="number" tick={{fontSize:9,fontFamily:F}} stroke="#D8DADC"
                        tickFormatter={v => v>=1000 ? `${v/1000}K` : v}/>
                      <YAxis type="category" dataKey="category_name" width={160}
                        tick={{fontSize:10,fontFamily:F}}
                        tickFormatter={v => v.length > 22 ? v.slice(0, 22) + '…' : v}/>
                      <Tooltip contentStyle={{fontSize:10,fontFamily:F}}
                        formatter={v => [v.toLocaleString(),"Exhibitors"]}/>
                      <Bar dataKey="total_exhibitors" fill="#E8431A" radius={[0,3,3,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                )
              }
            </div>

            {/* Cross-Event Opportunities */}
            <div style={{background:"#fff",borderRadius:10,border:"1px solid #D8DADC",padding:"16px 18px"}}>
              <div style={{fontSize:13,fontWeight:700,marginBottom:4}}>Cross-Event Opportunities</div>
              <div style={{fontSize:9,color:"#818084",marginBottom:10}}>
                Other ITE events that companies in this event's competitor shows are also AI-matched for
              </div>
              {crossEventChart.length === 0
                ? <EmptyState message="No cross-event data yet"/>
                : (
                  <ResponsiveContainer width="100%" height={Math.max(220, crossEventChart.length * 28)}>
                    <BarChart data={crossEventChart} layout="vertical"
                      margin={{left:4,right:20,top:0,bottom:0}} barCategoryGap="20%">
                      <XAxis type="number" tick={{fontSize:9,fontFamily:F}} stroke="#D8DADC"
                        tickFormatter={v => v>=1000 ? `${v/1000}K` : v}/>
                      <YAxis type="category" dataKey="event" width={160}
                        tick={{fontSize:9,fontFamily:F}}
                        tickFormatter={v => v.length > 22 ? v.slice(0, 22) + '…' : v}/>
                      <Tooltip contentStyle={{fontSize:10,fontFamily:F}}
                        formatter={v => [v.toLocaleString(),"Companies"]}/>
                      <Bar dataKey="count" fill="#960659" radius={[0,3,3,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                )
              }
            </div>

          </div>

          </>}

          </>)}

          </>)}
          {insightsTab === "geopolitical" && (
            <InsightsGeoSection
              filterEvent={filterEvent}
              filterYear={filterYear}
              filterShow={filterShow}
            />
          )}

        </div>
      </div>
    </PageShell>
  );
}
